if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();
        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();

        this.dispatchEvent(
          new CustomEvent('product-info:loaded', {
            bubbles: true,
          })
        );
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();

        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((el) =>
            el.classList.add('scroll-trigger--cancel')
          )
        );

        this.postProcessHtmlCallbacks.push(() => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({
        data: { event, target, selectedOptionValues },
      }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl =
          target.dataset.productUrl ||
          this.pendingRequestUrl ||
          this.dataset.url;

        this.pendingRequestUrl = productUrl;

        const shouldSwapProduct = this.dataset.url !== productUrl;

        const shouldFetchFullPage =
          this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(
            productUrl,
            selectedOptionValues,
            shouldFetchFullPage
          ),

          targetId: target.id,

          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.handleErrorMessage?.();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage
            ? "product-info[id^='MainProduct']"
            : 'product-info';

          const variant = this.getSelectedVariant(
            html.querySelector(selector)
          );

          this.updateURL(productUrl, variant?.id);

          HTMLUpdateUtility.viewTransition(
            updateFullPage ? document.querySelector('main') : this,
            html.querySelector(updateFullPage ? 'main' : 'product-info'),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();

        this.abortController = new AbortController();

        fetch(requestUrl, {
          signal: this.abortController.signal,
        })
          .then((r) => r.text())
          .then((text) => {
            this.pendingRequestUrl = null;

            const html = new DOMParser().parseFromString(
              text,
              'text/html'
            );

            callback(html);
          })
          .then(() => {
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch(console.error);
      }

      getSelectedVariant(node) {
        if (!node) return null;

        const json = node.querySelector(
          'variant-selects [data-selected-variant]'
        )?.innerHTML;

        return json ? JSON.parse(json) : null;
      }

      buildRequestUrlWithParams(url, optionValues, full = false) {
        const params = [];

        if (!full) {
          params.push(`section_id=${this.sectionId}`);
        }

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.updateVariantInputs(variant?.id);
          this.updateURL(productUrl, variant?.id);
          this.updateSubmitButton(variant);

          const priceSource = html.querySelector(
            `#price-${this.sectionId}`
          );

          const priceDestination = this.querySelector(
            `#price-${this.sectionId}`
          );

          if (priceSource && priceDestination) {
            priceDestination.innerHTML = priceSource.innerHTML;
          }

          const skuSource = html.querySelector(
            `#Sku-${this.sectionId}`
          );

          const skuDestination = this.querySelector(
            `#Sku-${this.sectionId}`
          );

          if (skuSource && skuDestination) {
            skuDestination.innerHTML = skuSource.innerHTML;

            skuDestination.classList.toggle(
              'visibility-hidden',
              skuSource.classList.contains('visibility-hidden')
            );
          }
        };
      }

      updateSubmitButton(variant) {
        const productForm = this.productForm;
        if (!productForm?.toggleSubmitButton) return;

        if (!variant) {
          productForm.toggleSubmitButton(
            true,
            window.variantStrings?.unavailable || 'Unavailable'
          );
          return;
        }

        if (!variant.available) {
          productForm.toggleSubmitButton(
            true,
            window.variantStrings?.soldOut || 'Sold out'
          );
          return;
        }

        productForm.toggleSubmitButton(false);
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((form) => {
          const input = form.querySelector('input[name="id"]');

          if (!input) return;

          input.value = variantId ?? '';

          input.dispatchEvent(
            new Event('change', {
              bubbles: true,
            })
          );
        });
      }

      updateURL(url, variantId) {
        if (this.dataset.updateUrl === 'false') return;

        window.history.replaceState(
          {},
          '',
          `${url}${variantId ? `?variant=${variantId}` : ''}`
        );
      }

      setQuantityBoundries() {}

      fetchQuantityRules() {}

      updateQuantityRules() {}

      get productForm() {
        return this.querySelector('product-form');
      }

      get sectionId() {
        return (
          this.dataset.originalSection ||
          this.dataset.section
        );
      }
    }
  );
}

/* =========================
   Shopify Thickness → Summary + Cart Property
   ========================= */

(function () {
  function formatThickness(thickness) {
    return thickness.replace('mm', '').trim() + ' (mm)';
  }

  function syncThickness(productInfo) {
    if (!productInfo) return;

    const checked = productInfo.querySelector(
      'variant-selects input[type="radio"]:checked'
    );

    const thickness = checked ? checked.value.trim() : '';

    if (!thickness) return;

    productInfo
      .querySelectorAll('.js-summary-thickness')
      .forEach((el) => {
        el.textContent = formatThickness(thickness);
        el.classList.remove('tcustomizer-placeholder-style');
      });

    const form = productInfo.querySelector(
      'form[action*="/cart/add"]'
    );

    if (!form) return;

    let input = form.querySelector(
      'input[name="properties[Thickness]"]'
    );

    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[Thickness]';
      form.appendChild(input);
    }

    input.value = thickness;
  }

  document.addEventListener('change', function (event) {
    const productInfo = event.target.closest('product-info');

    if (
      productInfo &&
      event.target.matches('variant-selects input[type="radio"]')
    ) {
      setTimeout(() => syncThickness(productInfo), 100);
    }
  });

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('product-info').forEach((productInfo) => {
      setTimeout(() => syncThickness(productInfo), 300);
    });
  });

  document.addEventListener('product-info:loaded', function (event) {
    const productInfo = event.target.closest('product-info') || event.target;

    setTimeout(() => syncThickness(productInfo), 300);
  });
})();